/**
 * Увеличивает контрастность hex-цвета, делая светлые цвета темнее, а темные - светлее.
 *
 * @param {string} hexColor - Цвет в формате HEX (#RRGGBB)
 * @param {number} [contrastFactor=1.5] - Коэффициент контрастности (1.0 - без изменений, >1.0 - усиление контраста)
 * @returns {string} - Новый HEX-цвет с увеличенной контрастностью
 *
 * @example
 * // Делает темный цвет светлее
 * enhanceContrast('#223366'); // => '#334999'
 *
 * // Делает светлый цвет темнее
 * enhanceContrast('#DDEECC'); // => '#93A188'
 */
export const enhanceContrast = (hexColor, contrastFactor = 1.5) => {
    // Проверка на валидность входных данных
    if (!hexColor || typeof hexColor !== 'string' || !hexColor.startsWith('#')) {
      console.warn('enhanceContrast: Неверный формат цвета', hexColor);
      return hexColor;
    }

    // Извлечение RGB компонентов из HEX-цвета
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);

    // Используем формулу относительной яркости из стандарта WCAG 2.0
    // Эта формула учитывает разную чувствительность человеческого глаза к разным цветам
    // Коэффициенты: красный (0.299), зеленый (0.587), синий (0.114)
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;

    // Определяем новые значения RGB на основе яркости
    let adjustedR, adjustedG, adjustedB;

    // Пороговое значение 128 выбрано как середина диапазона яркости (0-255)
    // Цвета со значением яркости выше 128 считаются светлыми, ниже - темными
    if (brightness > 128) {
      // Для светлых цветов: уменьшаем яркость, делая их темнее
      // Делим на contrastFactor, чтобы уменьшить значения RGB
      adjustedR = Math.max(0, Math.floor(r / contrastFactor));
      adjustedG = Math.max(0, Math.floor(g / contrastFactor));
      adjustedB = Math.max(0, Math.floor(b / contrastFactor));
    } else {
      // Для темных цветов: увеличиваем яркость, делая их светлее
      // Умножаем на contrastFactor, чтобы увеличить значения RGB
      adjustedR = Math.min(255, Math.floor(r * contrastFactor));
      adjustedG = Math.min(255, Math.floor(g * contrastFactor));
      adjustedB = Math.min(255, Math.floor(b * contrastFactor));
    }

    // Конвертируем обратно в HEX формат
    // padStart(2, '0') гарантирует, что каждая компонента будет двузначной
    const hexR = adjustedR.toString(16).padStart(2, '0');
    const hexG = adjustedG.toString(16).padStart(2, '0');
    const hexB = adjustedB.toString(16).padStart(2, '0');

    return `#${hexR}${hexG}${hexB}`;
  };


/**
 * Глубоко обходит дерево данных, находит ВСЕ корректные цвета
 * и группирует их по нормализованному hex-значению.
 *
 * Возвращает объект формата:
 * {
 *   "#FF00FF": [
 *     { key: "borderColor", parents: ["0"], original: "#FF00FF" }
 *   ],
 *   "#0000FF": [
 *     { key: "backgroundColor", parents: ["0"], original: "rgb(0,0,255)" }
 *   ],
 *   ...
 * }
 *
 * @param {unknown} input  объект / массив любой вложенности
 * @returns {Record<string, {key:string, parents:string[], original:string}[]>}
 */
export function collectUniqueColors(input) {
    /* ---------- константы и вспомогалки ---------- */

    const colorProp = /color$/i;                                   // имя свойства должно заканчиваться на "color"
    const hexRE = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
    const rgbRE = /^rgba?\(\s*(25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(25[0-5]|2[0-4]\d|1?\d?\d)(?:\s*,\s*(1|0|0?\.\d+))?\s*\)$/i;
    const toHexByte = n => Number(n).toString(16).padStart(2, '0').toUpperCase();

    /** true, если значение похоже на цвет и имя property подходит */
    function isColor(val, key) {
      if (!colorProp.test(key)) return false;                      // фильтруем по имени
      if (typeof val === 'string') return hexRE.test(val) || rgbRE.test(val);
      if (typeof val === 'number')  return Number.isInteger(val) && val >= 0x0 && val <= 0xFFFFFF;
      return false;
    }

    /** нормализует значение к hex + возвращает исходник */
    function normalize(val) {
      // 1) hex-строка
      if (typeof val === 'string' && hexRE.test(val)) {
        let h = val.toUpperCase();
        // #RGB[A]  →  #RRGGBB[AA]
        if (h.length === 4 || h.length === 5) {
          h = `#${  [...h.slice(1)].map(c => c + c).join('')}`;
        }
        return { hex: h, original: val };
      }

      // 2) rgb/rgba
      const m = typeof val === 'string' && val.match(rgbRE);
      if (m) {
        const [, r, g, b, a] = m;
        const alpha = a === undefined ? null : Math.round(parseFloat(a) * 255);
        const hex = `#${
          toHexByte(r)  }${toHexByte(g)  }${toHexByte(b)
          }${alpha !== null ? toHexByte(alpha) : ''}`;
        return { hex, original: val };
      }

      // 3) целое число 0xRRGGBB
      if (typeof val === 'number') {
        const hex = `#${  val.toString(16).padStart(6, '0').toUpperCase()}`;
        return { hex, original: String(val) };
      }

      // сюда не попадём благодаря isColor
      return { hex: String(val), original: String(val) };
    }

    /* ---------- обход и агрегация ---------- */

    const map = Object.create(null);                               // hex → массив записей
    const seen = new WeakSet();                                    // защита от циклов

    /** DFS по дереву */
    function walk(node, parents = []) {
      if (!node || typeof node !== 'object') return;
      if (seen.has(node)) return;
      seen.add(node);

      const entries = Array.isArray(node) ? node.entries() : Object.entries(node);

      for (const [rawKey, rawVal] of entries) {
        const key = String(rawKey);
        const val = rawVal;

        if (isColor(val, key)) {
          const { hex, original } = normalize(val);
          (map[hex] ||= []).push({ key, parents: parents.slice(), original });
        }

        if (val && typeof val === 'object') walk(val, parents.concat(key));
      }
    }

    walk(input);

    /* удаляем дубликаты внутри каждого hex (по пути+key+original) */
    for (const hex in map) {
      const uniq = new Map();
      for (const rec of map[hex]) {
        const sig = JSON.stringify([rec.key, rec.parents, rec.original]);
        uniq.set(sig, rec);
      }
      map[hex] = [...uniq.values()];
    }

    return map;
  }


  function hexToHSL(hex) {
    hex = hex.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16) / 255;
    const g = parseInt(hex.substring(2, 4), 16) / 255;
    const b = parseInt(hex.substring(4, 6), 16) / 255;

    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
        h = s = 0; // achromatic
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = (g - b) / d + (g < b ? 6 : 0); break;
            case g: h = (b - r) / d + 2; break;
            case b: h = (r - g) / d + 4; break;
        }
        h *= 60;
    }

    return { h, s: s * 100, l: l * 100 };
}

function hslToHex(h, s, l) {
    s /= 100;
    l /= 100;

    const k = n => (n + h / 30) % 12;
    const a = s * Math.min(l, 1 - l);
    const f = n => {
        const color = l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));
        return Math.round(255 * color).toString(16).padStart(2, '0');
    };

    return `#${f(0)}${f(8)}${f(4)}`;
}

export function enhanceColor(hex) {
    const { h, s, l } = hexToHSL(hex);

    const newS = Math.min(s * 1.3, 100); // насыщенность +30%
    const newL = Math.max(l * 0.6, 0);   // яркость уменьшить до 60%

    return hslToHex(h, newS, newL);
}
